import { useState, useMemo, useEffect } from "react";
import { Trophy, CheckCircle2, XCircle, RotateCcw, ArrowRight, Brain, Calendar } from "lucide-react";
import { toast } from "sonner";
import { bumpEngagement } from "@/lib/engagement";
import { api } from "@/lib/api";
import { claimOnce } from "@/lib/fanzone";
import StreakBadge from "@/components/StreakBadge";

const STORAGE_KEY = "titans_trivia_high_v1";

const FALLBACK_QUESTIONS = [
  {
    q: "In what year did the franchise officially become the Tennessee Titans?",
    options: ["1997", "1998", "1999", "2000"],
    a: 2,
    fact: "The Houston Oilers relocated in 1997, played two transitional seasons as the Tennessee Oilers, and were rebranded as the Titans for the 1999 season.",
  },
  {
    q: "Who threw the lateral in the Music City Miracle?",
    options: ["Steve McNair", "Frank Wycheck", "Kevin Dyson", "Lorenzo Neal"],
    a: 1,
    fact: "TE Frank Wycheck flipped the lateral across the field to WR Kevin Dyson, who returned it 75 yards for the game-winning touchdown vs. Buffalo on Jan. 8, 2000.",
  },
  {
    q: "Which team did the Titans face in Super Bowl XXXIV?",
    options: ["Baltimore Ravens", "St. Louis Rams", "Green Bay Packers", "Denver Broncos"],
    a: 1,
    fact: "The Rams won 23–16 on Kevin Dyson being tackled one yard short of the goal line — \"One Yard Short.\"",
  },
];

function shuffle(arr) {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

export default function TitansTrivia() {
  const [deck, setDeck] = useState([]);
  const [dateLabel, setDateLabel] = useState("");
  const [loadingDeck, setLoadingDeck] = useState(true);
  const [idx, setIdx] = useState(0);
  const [picked, setPicked] = useState(null);
  const [score, setScore] = useState(0);
  const [finished, setFinished] = useState(false);
  const [high, setHigh] = useState(() => parseInt(localStorage.getItem(STORAGE_KEY) || "0", 10));

  const loadDeck = async () => {
    setLoadingDeck(true);
    try {
      const { data } = await api.get("/trivia/daily");
      const qs = data?.questions || [];
      if (qs.length) {
        setDeck(shuffle(qs).slice(0, 10));
      } else {
        setDeck(shuffle(FALLBACK_QUESTIONS).slice(0, 10));
      }
      setDateLabel(data?.date || "");
    } catch {
      setDeck(shuffle(FALLBACK_QUESTIONS).slice(0, 10));
    } finally {
      setLoadingDeck(false);
    }
  };

  useEffect(() => { loadDeck(); }, []);

  const current = deck[idx];
  const total = deck.length;
  const progress = useMemo(() => (total ? ((idx + (picked !== null ? 1 : 0)) / total) * 100 : 0), [idx, picked, total]);

  useEffect(() => {
    if (finished && score > high) {
      localStorage.setItem(STORAGE_KEY, String(score));
      setHigh(score);
      toast.success(`New high score: ${score}/${total}!`);
    }
  }, [finished, score, high, total]);

  useEffect(() => {
    if (finished && dateLabel) claimOnce("trivia", dateLabel, { correct: score });
    if (finished) bumpEngagement("game", { dedupeKey: `trivia:${dateLabel}` });
  }, [finished, dateLabel, score]);

  const select = (i) => {
    if (picked !== null) return;
    setPicked(i);
    if (i === current.a) setScore((s) => s + 1);
  };

  const next = () => {
    if (idx + 1 >= deck.length) {
      setFinished(true);
    } else {
      setIdx((n) => n + 1);
      setPicked(null);
    }
  };

  const restart = () => {
    setIdx(0);
    setPicked(null);
    setScore(0);
    setFinished(false);
    loadDeck();
  };

  if (loadingDeck || !current) {
    return (
      <div data-testid="trivia-loading" className="titans-card p-8 text-titans-silver">
        Loading today&apos;s questions…
      </div>
    );
  }

  if (finished) {
    const pct = Math.round((score / total) * 100);
    const tier =
      pct >= 90
        ? { label: "Hall of Fame", color: "text-titans-red", emoji: "🏆" }
        : pct >= 70
        ? { label: "Two-Tone Diehard", color: "text-titans-blue", emoji: "🛡️" }
        : pct >= 50
        ? { label: "Sunday Regular", color: "text-yellow-400", emoji: "🏈" }
        : { label: "Bandwagon", color: "text-titans-silver", emoji: "📚" };

    return (
      <div data-testid="trivia-results" className="titans-card p-6 md:p-10">
        <div className="font-cond uppercase tracking-[0.25em] text-[10px] text-titans-blue inline-flex items-center gap-1">
          <Trophy size={12} /> Final Score
        </div>
        <div className="mt-4 grid md:grid-cols-3 gap-6 items-center">
          <div className="md:col-span-2">
            <div className="text-7xl">{tier.emoji}</div>
            <div className={`font-display text-4xl md:text-5xl uppercase tracking-wide mt-3 ${tier.color}`}>
              {tier.label}
            </div>
            <div className="text-titans-silver mt-2 font-body">
              You answered <span className="text-white font-bold">{score}</span> of {total} correct ({pct}%).
            </div>
            {score > 0 && score === high && (
              <div className="mt-2 text-titans-red font-cond uppercase tracking-wider text-xs">★ New personal best</div>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <button
              onClick={restart}
              data-testid="trivia-restart"
              className="bg-titans-red hover:bg-titans-red-hover font-cond uppercase tracking-wider text-sm px-5 py-3 inline-flex items-center justify-center gap-2"
            >
              <RotateCcw size={14} /> Play Again
            </button>
            <button
              onClick={() => {
                navigator.clipboard?.writeText(`I scored ${score}/${total} (${pct}%) on Titans Trivia — The Titan Standard. Beat that.`);
                toast.success("Result copied to clipboard");
              }}
              data-testid="trivia-share"
              className="border border-titans-blue text-titans-blue hover:bg-titans-blue hover:text-white font-cond uppercase tracking-wider text-sm px-5 py-3 transition-colors"
            >
              Share Score
            </button>
          </div>
        </div>
      </div>
    );
  }

  const isCorrect = picked === current.a;

  return (
    <div data-testid="trivia-quiz" className="titans-card p-6 md:p-8">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <div className="font-cond uppercase tracking-[0.25em] text-[10px] text-titans-blue inline-flex items-center gap-1">
            <Brain size={12} /> Titans Trivia · Daily
          </div>
          {dateLabel && (
            <div className="font-cond uppercase tracking-wider text-[10px] text-titans-silver mt-1 inline-flex items-center gap-1">
              <Calendar size={10} /> {new Date(dateLabel + "T00:00:00Z").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
            </div>
          )}
          <div className="font-cond uppercase tracking-wider text-[11px] text-titans-silver mt-2">
            Question <span className="text-white font-bold">{idx + 1}</span> / {total}
          </div>
        </div>
        <div className="text-right">
          <div className="font-cond uppercase tracking-[0.25em] text-[10px] text-titans-silver">Score</div>
          <div className="font-display text-3xl text-white">{score}</div>
          {high > 0 && (
            <div className="font-cond uppercase tracking-wider text-[9px] text-titans-blue mt-1">Best {high}</div>
          )}
          <div className="mt-2 flex justify-end">
            <StreakBadge
              storageKey="titans_trivia_streak_v1"
              triggerKey={finished && dateLabel ? dateLabel : null}
            />
          </div>
        </div>
      </div>

      <div className="h-1 bg-titans-void/60 mb-6">
        <div className="h-full bg-titans-red transition-[width] duration-500" style={{ width: `${progress}%` }} />
      </div>

      <h3 className="font-display text-2xl md:text-3xl uppercase tracking-wide leading-tight text-white">
        {current.q}
      </h3>

      <div className="grid sm:grid-cols-2 gap-3 mt-6">
        {current.options.map((opt, i) => {
          const isPicked = picked === i;
          const isAnswer = i === current.a;
          let cls = "border-[rgba(138,141,143,0.3)] hover:border-titans-blue text-white";
          if (picked !== null) {
            if (isAnswer) cls = "border-titans-red bg-titans-red/15 text-white";
            else if (isPicked) cls = "border-titans-silver bg-titans-silver/15 text-titans-silver line-through";
            else cls = "border-[rgba(138,141,143,0.15)] text-titans-silver/70";
          }
          return (
            <button
              key={i}
              onClick={() => select(i)}
              disabled={picked !== null}
              data-testid={`trivia-option-${i}`}
              className={`text-left border-2 px-4 py-3 font-body transition-colors ${cls}`}
            >
              <div className="flex items-start gap-3">
                <span className="font-display text-xl text-titans-blue shrink-0">{String.fromCharCode(65 + i)}</span>
                <span className="leading-snug">{opt}</span>
                {picked !== null && isAnswer && <CheckCircle2 size={18} className="ml-auto text-titans-red shrink-0" />}
                {picked !== null && isPicked && !isAnswer && <XCircle size={18} className="ml-auto text-titans-silver shrink-0" />}
              </div>
            </button>
          );
        })}
      </div>

      {picked !== null && (
        <div data-testid="trivia-explainer" className="mt-6 flex flex-col md:flex-row md:items-center gap-4 justify-between">
          <div className={`flex-1 border-l-4 ${isCorrect ? "border-titans-red" : "border-titans-blue"} pl-4`}>
            <div className={`font-cond uppercase tracking-wider text-xs ${isCorrect ? "text-titans-red" : "text-titans-blue"}`}>
              {isCorrect ? "Correct" : "Close, but no"}
            </div>
            <div className="text-titans-silver font-body text-sm mt-1">{current.fact}</div>
          </div>
          <button
            onClick={next}
            data-testid="trivia-next"
            className="bg-titans-red hover:bg-titans-red-hover font-cond uppercase tracking-wider text-sm px-5 py-3 inline-flex items-center gap-2"
          >
            {idx + 1 >= deck.length ? "See Result" : "Next"} <ArrowRight size={14} />
          </button>
        </div>
      )}
    </div>
  );
}
